import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import NodeSkeleton from "./NodeSkeleton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeListRow, { NodeRowChevron } from "./NodeListRow";
import NodeToolbar from "./NodeToolbar";

function BomLinesListNode({ data }: NodeProps<ExplorerNode>) {
    const lines = data.bomLines ?? [];
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            source={data.source}
            minWidth={220}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
            />

            {data.bomNote && (
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        display: "block",
                        fontStyle: "italic",
                        lineHeight: 1.3,
                        mt: 0.25,
                        mb: 0.25,
                        ml: 2.5,
                        maxWidth: 260,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                    }}
                >
                    {data.bomNote}
                </Typography>
            )}

            {isLoading ? (
                <NodeSkeleton rows={3} variant="list" />
            ) : lines.length === 0 ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    No BOM lines
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {lines.map((line, index) => (
                        <React.Fragment key={`${line.itemNumber}-${index}`}>
                            {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <NodeListRow
                                nodeColor={nodeColor}
                                isExpanded={line.isExpanded}
                                onClick={() => data.onBomLineSelect?.(line.itemNumber, line.dataAreaId)}
                            >
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 400,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {line.itemNumber}
                                    </Typography>
                                    {line.stdSailItem && (
                                        <Tooltip title="Standard Sail Item (phantom)" arrow>
                                            <Chip
                                                label="P"
                                                size="small"
                                                sx={{
                                                    height: 16,
                                                    minWidth: 20,
                                                    fontSize: "0.65rem",
                                                    fontWeight: 700,
                                                    backgroundColor: `${nodeColor}20`,
                                                    color: nodeColor,
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    {line.lineType === "Vendor" && (
                                        <Tooltip title={`Vendor: ${line.vendorAccountNumber}`} arrow>
                                            <Chip
                                                label="V"
                                                size="small"
                                                sx={{
                                                    height: 16,
                                                    minWidth: 20,
                                                    fontSize: "0.65rem",
                                                    fontWeight: 700,
                                                    backgroundColor: "#ff980020",
                                                    color: "#ff9800",
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "0.7rem",
                                        }}
                                    >
                                        {line.quantity} {line.productUnitSymbol}
                                    </Typography>
                                    <NodeRowChevron isExpanded={line.isExpanded} nodeColor={nodeColor} />
                                </Stack>
                            </NodeListRow>
                        </React.Fragment>
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(BomLinesListNode);
